import '@theme/tsd-set-gallery';
import { SetInquiryForms } from '@theme/tsd-set-inquiry-forms';
import { Component } from '@theme/component';
import { selectSize, voucherSelection, inquirySummary, baseItems, productCardData } from '@theme/tsd-table-set-data';

const productRequests = new Map();

class TableSet extends Component {
  #nativeInquiry;
  #previewSets;
  #previewLoading = false;
  #controller;
  #data = null;
  #draftRestored = false;
  #appliedCoupon = '';
  #draftFields = ['name', 'email', 'phone', 'Abholung', 'Rückgabe', 'Nachricht'];

  get #draftKey() {
    return `tsd-table-set:${this.dataset.pageId}:${this.dataset.sectionId}`;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#nativeInquiry?.destroy();
  }

  #initialize() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#nativeInquiry?.destroy();
    const nativeMount = this.querySelector('[data-set-native-form]');
    this.#nativeInquiry = nativeMount ? new SetInquiryForms(nativeMount) : null;
    try {
      this.#data = JSON.parse(this.querySelector('[data-set-config]')?.textContent || 'null');
    } catch {
      this.#data = null;
    }
    const form = this.querySelector('form');
    if (form?.querySelector('[data-set-success]')) {
      try { sessionStorage.removeItem(this.#draftKey); } catch { /* Storage may be unavailable. */ }
    } else if (!this.#draftRestored) {
      this.#restoreDraft(form);
      this.#draftRestored = true;
    }
    const { signal } = this.#controller;
    this.addEventListener('change', event => {
      if (nativeMount && event.composedPath().includes(nativeMount)) return;
      if (event.target.matches('[data-preview-set]')) this.#applyPreview(event.target.value);
      else {
        if (event.target.matches('[data-guests], [data-tables]')) this.#resetCoupon();
        this.#render();
      }
    }, { signal });
    this.querySelector('[data-copy-coupon]')?.addEventListener('click', () => this.#copyCoupon(), { signal });
    this.querySelector('[data-apply-coupon]')?.addEventListener('click', () => this.#applyCoupon(), { signal });
    this.querySelector('[data-coupon-input]')?.addEventListener('keydown', event => {
      if (event.key === 'Enter') { event.preventDefault(); this.#applyCoupon(); }
    }, { signal });
    this.querySelector('[data-coupon-input]')?.addEventListener('input', () => {
      if (this.#appliedCoupon) { this.#appliedCoupon = ''; this.#render(); }
      this.querySelector('[data-coupon-status]').textContent = '';
    }, { signal });
    if (!this.dataset.pageId && !this.#previewLoading) this.#loadPreview();
    this.querySelector('form')?.addEventListener('submit', event => {
      this.#render();
      if (!this.#selection() || !event.currentTarget.reportValidity()) {
        event.preventDefault();
      } else {
        this.#saveDraft(event.currentTarget);
      }
    }, { signal });
    if (!this.dataset.pageId && this.#previewSets) this.#showPreview();
    else this.#render();
  }

  async #loadPreview() {
    this.#previewLoading = true;
    try {
      const response = await fetch(this.dataset.previewSrc, { signal: AbortSignal.timeout(10000) });
      if (!response.ok) return;
      this.#previewSets = await response.json();
      if (!this.isConnected || !Array.isArray(this.#previewSets)) return;
      this.#showPreview();
    } catch { /* The regular page stays usable if the preview file cannot load. */ }
  }

  #showPreview() {
    const select = this.querySelector('[data-preview-set]');
    if (!select || !this.#previewSets.length) return;
    select.replaceChildren(...this.#previewSets.map(set => new Option(set.title, set.id)));
    const requested = new URL(location.href).searchParams.get('tischset');
    select.value = this.#previewSets.some(set => set.id === requested) ? requested : this.#previewSets[0].id;
    this.querySelector('[data-preview-picker]').hidden = false;
    this.#applyPreview(select.value);
  }

  #applyPreview(id) {
    const set = this.#previewSets?.find(value => value.id === id);
    if (!set) return;
    this.#data = set.config;
    this.dataset.title = set.title;
    this.querySelector('[data-set-title]').textContent = set.title;
    this.querySelector('[data-set-intro]').textContent = set.intro;
    this.querySelector('[data-set-config]').textContent = JSON.stringify(set.config);
    this.querySelector('[data-guests]').value = '4';
    this.querySelector('[data-tables]').value = '';
    this.#resetCoupon();
    this.#renderPreviewImages(set);
    const url = new URL(location.href);
    url.searchParams.set('tischset', id);
    history.replaceState(null, '', url);
    this.#render();
  }

  #renderPreviewImages(set) {
    this.querySelector('tsd-set-gallery')?.setPhotos([set.image, ...(set.gallery || [])]);
  }

  async #hydrateItem(row, item) {
    if (!/^[a-z0-9][a-z0-9-]*$/.test(item.productHandle || '') || !/^\d+$/.test(String(item.variantId || ''))) return;
    const url = `${this.dataset.rootUrl}products/${item.productHandle}`;
    if (!productRequests.has(url)) {
      productRequests.set(url, fetch(`${url}.js`, { signal: AbortSignal.timeout(10000) })
        .then(response => response.ok ? response.json() : null).catch(() => null));
    }
    const card = productCardData(await productRequests.get(url), item);
    if (!card || !row.isConnected) return;
    const link = row.querySelector('[data-product-link]');
    if (!card.unpublished) {
      link.href = `${url}?variant=${item.variantId}`;
      row.querySelector('[data-product-detail]').hidden = false;
    }
    row.querySelector('[data-product-title]').textContent = card.title;
    row.querySelector('[data-product-variant]').textContent = card.variant;
    if (!card.image) return;
    const image = document.createElement('img');
    const source = new URL(card.image, location.origin);
    source.searchParams.set('width', '420');
    image.src = source.href;
    image.alt = '';
    image.loading = 'lazy';
    image.width = 420;
    image.height = 420;
    const placeholder = row.querySelector('[data-product-image] > span');
    image.addEventListener('load', () => { placeholder.hidden = true; }, { once: true });
    image.addEventListener('error', () => {
      image.remove();
      placeholder.hidden = false;
    }, { once: true });
    row.querySelector('[data-product-image]').append(image);
  }

  #saveDraft(form) {
    const draft = { guests: this.querySelector('[data-guests]').value, tables: this.#selection()?.tables || 1,
      coupon: this.#appliedCoupon, savedAt: Date.now(), fields: {} };
    for (const name of this.#draftFields) draft.fields[name] = form.elements.namedItem(`contact[${name}]`).value;
    try { sessionStorage.setItem(this.#draftKey, JSON.stringify(draft)); } catch { /* Native submission still works. */ }
  }

  #restoreDraft(form) {
    if (!form?.querySelector('fieldset')) return;
    try {
      const draft = JSON.parse(sessionStorage.getItem(this.#draftKey) || 'null');
      if (!draft || typeof draft.savedAt !== 'number' || Date.now() - draft.savedAt > 30 * 60 * 1000) {
        sessionStorage.removeItem(this.#draftKey);
        return;
      }
      if (selectSize(this.#data, draft.guests, draft.tables)) {
        this.querySelector('[data-guests]').value = draft.guests;
        this.querySelector('[data-tables]').value = String(draft.tables || 1);
        if (voucherSelection(this.#data, this.#selection(), draft.coupon)?.appliedCoupon) {
          this.#appliedCoupon = draft.coupon;
          this.querySelector('[data-coupon-input]').value = draft.coupon;
        }
      }
      for (const name of this.#draftFields) {
        if (typeof draft.fields?.[name] === 'string') form.elements.namedItem(`contact[${name}]`).value = draft.fields[name];
      }
    } catch { /* Ignore an unavailable or malformed draft. */ }
  }

  #selection() {
    const guests = this.querySelector('[data-guests]')?.value;
    return selectSize(this.#data, guests, Number(guests) >= 8 ? this.querySelector('[data-tables]')?.value : 1);
  }

  #resetCoupon() {
    this.#appliedCoupon = '';
    const input = this.querySelector('[data-coupon-input]');
    if (input) input.value = '';
    for (const node of this.querySelectorAll('[data-coupon-status], [data-copy-status]')) node.textContent = '';
  }

  async #copyCoupon() {
    const code = this.#data?.pricing?.voucherCode;
    if (!code || !this.#selection()?.discountPercent) return;
    try {
      await navigator.clipboard.writeText(code);
      this.querySelector('[data-copy-status]').textContent = this.dataset.copySuccess;
      this.querySelector('[data-coupon-input]').focus();
    } catch {
      this.querySelector('[data-copy-status]').textContent = this.dataset.copyFailed;
    }
  }

  #applyCoupon() {
    const size = this.#selection();
    const selected = voucherSelection(this.#data, size, this.querySelector('[data-coupon-input]').value);
    this.#appliedCoupon = selected?.appliedCoupon || '';
    this.querySelector('[data-coupon-status]').textContent = this.#appliedCoupon ? '' : this.dataset.couponInvalid;
    this.#render();
  }

  #render() {
    const guests = this.querySelector('[data-guests]');
    for (const option of guests.options) {
      option.hidden = option.disabled = this.#data?.ready === true && !selectSize(this.#data, option.value);
    }
    if (guests.selectedOptions[0]?.disabled) {
      const first = [...guests.options].find(option => !option.disabled);
      if (first) guests.value = first.value;
    }
    const tables = this.querySelector('[data-tables]');
    const chooseTables = Number(guests.value) >= 8;
    this.querySelector('[data-table-choice]').hidden = !chooseTables;
    if (!chooseTables) tables.value = '';
    const needsTables = this.#data?.ready === true && chooseTables && !tables.value;
    const rawSize = this.#selection();
    const size = voucherSelection(this.#data, rawSize, this.#appliedCoupon);
    const money = new Intl.NumberFormat(document.documentElement.lang || 'de', {
      style: 'currency', currency: 'EUR',
    });
    const formatted = size ? money.format(size.priceCents / 100)
      : needsTables ? this.dataset.tableRequiredPrice : this.dataset.pendingPrice;
    const regular = size?.regularPriceCents ? money.format(size.regularPriceCents / 100) : '';
    this.querySelector('[data-price]').textContent = formatted;
    this.querySelector('[data-regular-price]').textContent = regular;
    this.querySelector('[data-regular-price-row]').hidden = !size?.discountPercent;
    this.querySelector('[data-rental-discount]').hidden = !size?.discountPercent;
    const eligible = this.#data?.pricing?.mode === 'manual-voucher' && rawSize?.discountPercent > 0;
    this.querySelector('[data-rental-voucher]').hidden = !eligible;
    if (eligible) {
      this.querySelector('[data-coupon-code]').textContent = this.#data.pricing.voucherCode;
      this.querySelector('[data-voucher-price]').textContent = money.format(rawSize.discountedPriceCents / 100);
      const savings = money.format((rawSize.regularPriceCents - rawSize.discountedPriceCents) / 100);
      this.querySelector('[data-voucher-saving]').textContent = this.dataset.couponSaving.replace('{amount}', savings);
      this.querySelector('[data-apply-coupon]').disabled = Boolean(size.appliedCoupon);
      if (size.appliedCoupon) this.querySelector('[data-coupon-status]').textContent = this.dataset.couponApplied.replace('{amount}', savings);
    }
    this.querySelector('[data-status]').textContent = size ? this.dataset.readyNote
      : needsTables ? this.dataset.tableRequiredNote : this.dataset.pendingNote;
    const items = size?.items || baseItems(this.#data?.ready ? { ...this.#data, ready: false } : this.#data);
    this.querySelector('[data-items-pending]').hidden = items.length > 0;
    this.querySelector('[data-base-items-note]').hidden = Boolean(size) || items.length === 0;
    const list = this.querySelector('[data-items]');
    list.replaceChildren();
    for (const item of items) {
      const row = this.querySelector('[data-product-card]').content.firstElementChild.cloneNode(true);
      const reference = this.#data?.baseItems?.find(base => base.title === item.title && (item.sku === undefined || base.sku === item.sku));
      const linkedItem = { ...reference, ...item };
      row.dataset.variantId = linkedItem.variantId || '';
      row.querySelector('[data-product-quantity]').textContent = `${item.quantity} ×`;
      row.querySelector('[data-product-title]').textContent = item.title;
      list.append(row);
      this.#hydrateItem(row, linkedItem);
    }
    if (this.#nativeInquiry) {
      const summary = inquirySummary(this.dataset.title, size, formatted, regular);
      this.#nativeInquiry.update({ ready: Boolean(size), summary: size ? summary : '' });
      return;
    }
    const form = this.querySelector('form');
    if (!form?.querySelector('fieldset')) return;

    form.querySelector('[name="contact[Personen]"]').value = size?.guests || '';
    form.querySelector('[name="contact[Tischset]"]').value = this.dataset.title;
    form.querySelector('[name="contact[Tische]"]').value = size?.tables || '';
    form.querySelector('[name="contact[Regulärer Paketmietpreis]"]').value = regular;
    form.querySelector('[name="contact[Weihnachtsrabatt]"]').value = size?.discountPercent ? `${size.discountPercent} %` : '';
    form.querySelector('[name="contact[Gutscheincode]"]').value = size?.appliedCoupon || '';
    form.querySelector('[name="contact[Gutscheinersparnis]"]').value = size?.savingsCents ? money.format(size.savingsCents / 100) : '';
    form.querySelector('[name="contact[Paketmietpreis]"]').value = size ? formatted : '';
    form.querySelector('[name="contact[body]"]').value = inquirySummary(this.dataset.title, size, formatted, regular);
    const pickup = form.querySelector('[data-pickup]');
    const returned = form.querySelector('[data-return]');
    returned.min = pickup.value || '';
    form.querySelector('fieldset').disabled = !size;
    form.querySelector('[type="submit"]').disabled = !size;
    returned.setCustomValidity(pickup.value && returned.value && returned.value < pickup.value
      ? this.dataset.dateError : '');
  }
}

if (!customElements.get('tsd-table-set')) customElements.define('tsd-table-set', TableSet);
